import { Alert, AlertIcon, useDisclosure, SlideFade } from '@chakra-ui/react';
import { Button } from '@chakra-ui/button';
import { Box, Flex, VStack } from '@chakra-ui/layout';
import TextField from '@material-ui/core/TextField';
import * as React from 'react';
import {
  useAcquireAddressState,
  usePlaceSuggestions,
} from './AcquireAddressStateProvider';
import { useAddressToTheDoorData } from './AddressStateProvider';
import { EditableSelect } from './EditableSelect';
import { okhiTracker } from '../OkHiTracker';
import { useTimeout } from '../hooks/useTimeout';
import { useOkHiIncomingMessage } from './OkHiIncomingMessageProvider';

const getTextFieldProps = (value: string) => ({
  fullWidth: true,
  variant: 'outlined' as const,
  InputLabelProps: {
    shrink: Boolean(value),
  },
  inputProps: {
    style: {
      fontSize: '16px',
    },
  },
  InputProps: {
    style: {
      borderRadius: 4,
      height: 56,
    },
  },
});

export function ToTheDoorScreen() {
  const okhiMessage = useOkHiIncomingMessage();
  const { setAcquireAddressState } = useAcquireAddressState();
  const { addressToTheDoorData, setAddressToTheDoorData } =
    useAddressToTheDoorData();

  const [roadName, setRoadName] = React.useState(
    addressToTheDoorData?.streetName ?? addressToTheDoorData?.roadName ?? '',
  );
  const [roadNameTouched, setRoadNameTouched] = React.useState(false);

  const [propertyNumber, setPropertyNumber] = React.useState<string>(
    addressToTheDoorData?.propertyNumber ?? '',
  );

  const [unit, setUnit] = React.useState<string>(
    addressToTheDoorData?.unit ??
      addressToTheDoorData?.apartment ??
      addressToTheDoorData?.unitNameOrNumber ??
      '',
  );

  const [buildingName, setBuildingName] = React.useState<string>(
    addressToTheDoorData?.buildingOrEstateName ??
      addressToTheDoorData?.buildingName ??
      addressToTheDoorData?.estate ??
      '',
  );
  const [buildingNameTouched, setBuildingNameTouched] = React.useState(false);

  const [directionsFromLandmarks, setDirectionsFromLandmarks] =
    React.useState<string>(
      addressToTheDoorData?.directionsFromLandmarks ??
        addressToTheDoorData?.otherInfo ??
        '',
    );

  const { status, data: placeSuggestions } = usePlaceSuggestions();

  const roadNameOptions = React.useMemo(() => {
    const roadNameSuggestions = placeSuggestions?.roadNames || [];
    return roadNameSuggestions.filter((roadNameSuggestion) => {
      return roadNameSuggestion !== roadName;
    });
  }, [placeSuggestions, roadName]);

  const buildingNameOptions = React.useMemo(() => {
    const buildingNameSuggestions = placeSuggestions?.buildingNames || [];
    return buildingNameSuggestions.filter(
      (bNameSuggestion) => bNameSuggestion !== buildingName,
    );
  }, [placeSuggestions, buildingName]);

  const isBuildingNameRequired =
    okhiMessage?.context.requiredFieldsConfig.buildingNameOrNumber === true;

  const isDoneDisabled = !roadName || (isBuildingNameRequired && !buildingName);

  const { isOpen: isPayoffAlertOpen, onClose: onClosePayoffAlert } =
    useDisclosure({ defaultIsOpen: true });

  useTimeout(onClosePayoffAlert, 1500);

  React.useEffect(() => {
    okhiTracker.page();
  }, []);

  React.useEffect(() => {
    if (!roadName && !roadNameTouched && roadNameOptions.length > 0) {
      setRoadName(roadNameOptions[0]);
    }
  }, [roadName, roadNameOptions, roadNameTouched]);

  React.useEffect(() => {
    if (
      !buildingName &&
      !buildingNameTouched &&
      buildingNameOptions.length > 0
    ) {
      setBuildingName(buildingNameOptions[0]);
    }
  }, [buildingName, buildingNameOptions, buildingNameTouched]);

  const handleSave = () => {
    setAddressToTheDoorData({
      addressType: 'home',
      type: 'home',
      roadName,
      streetName: roadName,
      propertyNumber,
      unit,
      buildingOrEstateName: buildingName,
      directionsFromLandmarks,
      propertyName: buildingName || undefined,
      directions: directionsFromLandmarks || undefined,
      unitNameOrNumber: unit || undefined,
      buildingName,
    });

    setAcquireAddressState('complete');

    okhiTracker.track('Button Tapped', {
      onObject: 'button',
      action: 'tapclick',
      label: 'Done',
    });

    okhiTracker.track('Form Submitted', {
      onObject: 'toTheDoorForm',
      action: 'submit',
      addressType: 'home',
      streetName: roadName,
      propertyNumber: propertyNumber || undefined,
      propertyName: buildingName || undefined,
      unit: unit || undefined,
      directions: directionsFromLandmarks || undefined,
    });
  };

  return (
    <Flex h="100%" direction="column">
      <SlideFade in={isPayoffAlertOpen} unmountOnExit={true} offsetY="-20px">
        <Alert status="success" variant="solid">
          <AlertIcon />
          Map pin saved
        </Alert>
      </SlideFade>

      <Flex px={6} py={4} direction="column" position="relative">
        <VStack spacing={6} align="stretch">
          <Flex gap={4} align="stretch">
            <Box flex="0 0 30%">
              <TextField
                label="Number"
                value={propertyNumber}
                onChange={(ev) => setPropertyNumber(ev.target.value)}
                {...getTextFieldProps(propertyNumber)}
              />
            </Box>

            <Box flex="1" minW={0}>
              <EditableSelect
                value={roadName}
                onChange={(roadName) => {
                  setRoadNameTouched(true);
                  setRoadName(roadName);
                }}
                options={roadNameOptions}
                placeholder="Street name"
                isLoading={status === 'loading'}
                autoFocus={true}
                isRequired
              />
            </Box>
          </Flex>

          <Flex gap={4} align="stretch">
            <Box flex="0 0 30%">
              <TextField
                label="Unit"
                value={unit}
                onChange={(ev) => setUnit(ev.target.value)}
                {...getTextFieldProps(unit)}
              />
            </Box>

            <Box flex="1" minW={0}>
              <EditableSelect
                value={buildingName}
                onChange={(buildingName) => {
                  setBuildingNameTouched(true);
                  setBuildingName(buildingName);
                }}
                options={buildingNameOptions}
                placeholder="Building/estate name"
                isLoading={status === 'loading'}
                isRequired={isBuildingNameRequired}
              />
            </Box>
          </Flex>

          <Box>
            <Box>
              <TextField
                label="Directions from landmarks"
                value={directionsFromLandmarks}
                onChange={(ev) => setDirectionsFromLandmarks(ev.target.value)}
                fullWidth
                variant="outlined"
                multiline
                rows={5}
                InputLabelProps={{
                  shrink: Boolean(directionsFromLandmarks),
                }}
                inputProps={{
                  style: {
                    fontSize: '16px',
                    lineHeight: 1.5,
                  },
                }}
                InputProps={{
                  style: {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Box>
        </VStack>

        <Button
          mt={6}
          variant="okhi-solid"
          disabled={isDoneDisabled}
          onClick={handleSave}
        >
          Done
        </Button>
      </Flex>
    </Flex>
  );
}
